/**
 * Welcome / Home dashboard — alternative opening screen.
 * Layout (top → bottom):
 *  1. One-line inspirational quote (rotates daily, 400-quote loop)
 *  2. Today's 3-4 scheduled habits (tap a row → /today; "View all" button)
 *  3. Stats summary card (tap → /stats)
 *
 * Honors user_prefs.home_screen. A small "Show Today by default" toggle in
 * the top-right lets users flip back to the Today-first opening.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useUserPrefs } from '../contexts/UserPrefsContext';
import { quoteForDay } from '../lib/quotes';
import { calculateStreaks } from '../lib/streaks';
import { isScheduledOn, todayISO } from '../lib/dates';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowRight, Flame, TrendingUp, CheckCircle2, Sparkles } from 'lucide-react';

const toIconKey = (n) => (n || 'sparkles').split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');
const TOP_N = 4;

export default function Home() {
  const { user } = useAuth();
  const { prefs, setPref } = useUserPrefs();
  const navigate = useNavigate();
  const [habits, setHabits] = useState([]);
  const [logsToday, setLogsToday] = useState({});
  const [allLogsByHabit, setAllLogsByHabit] = useState({});
  const [loading, setLoading] = useState(true);

  const date = todayISO();
  const quote = useMemo(() => quoteForDay(new Date()), []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const [{ data: h }, { data: today }, { data: all }] = await Promise.all([
        supabase.from('habits').select('*').is('archived_at', null).order('sort_order').order('created_at'),
        supabase.from('habit_logs').select('*').eq('log_date', date),
        supabase.from('habit_logs').select('habit_id,log_date,status,value'),
      ]);
      if (cancelled) return;
      setHabits(h || []);
      const t = {}; for (const l of today || []) t[l.habit_id] = l;
      setLogsToday(t);
      const m = {}; for (const l of all || []) { (m[l.habit_id] = m[l.habit_id] || []).push(l); }
      setAllLogsByHabit(m);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, date]);

  const scheduledToday = useMemo(
    () => habits.filter((h) => isScheduledOn(h, new Date())),
    [habits],
  );

  const isCompleted = useCallback((h) => {
    const l = logsToday[h.id];
    if (!l) return false;
    if (h.type === 'binary') return l.status === 'done';
    return Number(l.value || 0) >= Number(h.target_value || 1);
  }, [logsToday]);

  const total = scheduledToday.length;
  const done = scheduledToday.filter(isCompleted).length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  // Top unfinished (or just top if all done)
  const previewHabits = useMemo(() => {
    const unfinished = scheduledToday.filter((h) => !isCompleted(h));
    const pool = unfinished.length > 0 ? unfinished : scheduledToday;
    return pool.slice(0, TOP_N);
  }, [scheduledToday, isCompleted]);

  // Stats summary: total active habits, total logs, best streak across habits
  const stats = useMemo(() => {
    let totalLogs = 0;
    let bestStreak = 0;
    let activeStreaks = 0;
    for (const h of habits) {
      const logs = allLogsByHabit[h.id] || [];
      totalLogs += logs.length;
      const { current, longest } = calculateStreaks(h, logs);
      if (longest > bestStreak) bestStreak = longest;
      if (current > 0) activeStreaks += 1;
    }
    return { activeHabits: habits.length, totalLogs, bestStreak, activeStreaks };
  }, [habits, allLogsByHabit]);

  const flipToToday = async () => {
    await setPref('home_screen', 'today');
    navigate('/today', { replace: true });
  };

  return (
    <div className="space-y-10" data-testid="home-page">
      {/* Top toolbar */}
      <div className="flex items-center justify-between">
        <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">Welcome</p>
        <button
          type="button"
          onClick={flipToToday}
          className="text-xs text-muted-foreground hover:text-foreground rounded-full px-3 py-1 hover:bg-muted transition-colors"
          data-testid="home-switch-to-today"
        >
          Open Today by default →
        </button>
      </div>

      {/* Quote */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="text-center max-w-2xl mx-auto"
        data-testid="home-quote"
      >
        <Sparkles className="h-4 w-4 mx-auto text-primary mb-3" strokeWidth={1.5} />
        <p className="font-heading text-xl sm:text-2xl leading-snug tracking-tight">
          &ldquo;{quote.text}&rdquo;
        </p>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-3" data-testid="home-quote-author">
          — {quote.author}
        </p>
      </motion.section>

      {/* Today's habits preview */}
      <section data-testid="home-today-section">
        <div className="flex items-end justify-between mb-3 px-1">
          <div>
            <p className="uppercase tracking-[0.2em] text-[11px] text-muted-foreground font-medium">Today</p>
            <h2 className="font-heading text-lg font-medium">
              {total === 0 ? 'No habits scheduled' : `${done} of ${total} done`}
            </h2>
          </div>
          <Link
            to="/today"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            data-testid="home-view-all-today"
          >
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-border bg-card/30 p-1 space-y-1">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}
          </div>
        ) : previewHabits.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card/30 p-6 text-center text-sm text-muted-foreground">
            Nothing scheduled today. Enjoy the calm.
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card/30 overflow-hidden">
            {previewHabits.map((h) => {
              const I = LucideIcons[toIconKey(h.icon)] || LucideIcons.Sparkles;
              const completed = isCompleted(h);
              const streak = calculateStreaks(h, allLogsByHabit[h.id] || []).current;
              return (
                <Link
                  key={h.id}
                  to="/today"
                  className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors"
                  data-testid={`home-habit-${h.id}`}
                >
                  <div
                    className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: completed ? `${h.color}` : `${h.color}1F`, color: completed ? '#fff' : h.color }}
                  >
                    {completed ? <CheckCircle2 className="h-5 w-5" /> : <I className="h-5 w-5" strokeWidth={1.6} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-heading text-sm flex items-center gap-2 ${completed ? 'line-through text-muted-foreground' : ''}`}>
                      {h.name}
                      {streak > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-secondary">
                          <Flame className="h-3 w-3" />{streak}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground capitalize">{h.time_of_day} · {h.type}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
                </Link>
              );
            })}
            {total > previewHabits.length && (
              <Link to="/today" className="block px-4 py-2.5 text-xs text-center text-muted-foreground hover:bg-muted/40 transition-colors" data-testid="home-more-today">
                + {total - previewHabits.length} more
              </Link>
            )}
          </div>
        )}

        {total > 0 && (
          <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-4">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} aria-hidden />
          </div>
        )}
      </section>

      {/* Stats summary */}
      <Link to="/stats" className="block group" data-testid="home-stats-link">
        <div className="rounded-2xl border border-border bg-card/30 p-5 sm:p-6 hover:border-primary/40 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="uppercase tracking-[0.2em] text-[11px] text-muted-foreground font-medium">Stats</p>
              <h2 className="font-heading text-lg font-medium">Your flow at a glance</h2>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary transition-colors" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Active habits" value={stats.activeHabits} testid="stat-active" />
            <StatCard label="Total logs" value={stats.totalLogs} testid="stat-logs" />
            <StatCard label="Active streaks" value={stats.activeStreaks} icon={<Flame className="h-3 w-3 text-secondary" />} testid="stat-active-streaks" />
            <StatCard label="Best streak" value={stats.bestStreak} icon={<TrendingUp className="h-3 w-3 text-primary" />} testid="stat-best" />
          </div>
        </div>
      </Link>

      {/* Footer toggle reminder for first-time visitors */}
      {prefs.home_screen !== 'welcome' && (
        <p className="text-center text-[11px] text-muted-foreground">
          This is a preview. Make Welcome your default in{' '}
          <Link to="/settings" className="underline hover:text-foreground" data-testid="home-go-settings">Settings</Link>.
        </p>
      )}
    </div>
  );
}

const StatCard = ({ label, value, icon, testid }) => (
  <div className="rounded-xl bg-background/40 px-3 py-2.5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
      {icon}{label}
    </div>
    <div className="font-heading text-2xl mt-1">{value}</div>
  </div>
);
